import { ClockCircleOutlined } from "@ant-design/icons";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import { Button, Popover } from "antd";
import type { InputProps } from "antd";
import moment from "moment";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { PickerInput, PickerPanel } from "./styled";

export type ExtendedTimeDisabledData = {
  disabledHours?: () => number[] | void;
  disabledMinutes?: (hour: number) => number[] | void;
  disabledSeconds?: (hour: number, minute: number) => number[] | void;
};

export type ExtendedTimePickerProps = Omit<
  InputProps,
  "value" | "defaultValue" | "onChange" | "onKeyDown" | "type"
> & {
  value?: string | null;
  defaultValue?: string | null;
  onChange?: (value: string) => void;
  disabledTime?: () => ExtendedTimeDisabledData | boolean | void;
  extendedTimeHourCount?: number;
};

const MINUTES = Array.from({ length: 60 }, (_, index) => index);
const pad = (value: number) => String(value).padStart(2, "0");

export const normalizeExtendedTime = (value: unknown): string => {
  if (value == null || value === "") return "";
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format(DATE_TIME_FORMAT.TIME) : "";
  }
  const text = String(value).trim();
  const match = text.match(/^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/);
  if (match) return `${pad(Number(match[1]))}:${match[2]}`;
  const time = moment(text, moment.ISO_8601, true);
  return time.isValid() ? time.format(DATE_TIME_FORMAT.TIME) : "";
};

const ExtendedTimePicker = ({
  value: controlledValue,
  defaultValue,
  onChange,
  disabledTime,
  disabled: inputDisabled = false,
  allowClear = true,
  placeholder = "時間を選択",
  extendedTimeHourCount = 44,
  ...inputProps
}: ExtendedTimePickerProps) => {
  const listHours = Array.from(
    { length: extendedTimeHourCount },
    (_, index) => index,
  );

  const [internalValue, setInternalValue] = useState(() =>
    normalizeExtendedTime(defaultValue),
  );
  const isControlled = controlledValue !== undefined;
  const value = normalizeExtendedTime(
    isControlled ? controlledValue : internalValue,
  );
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  const hourColumn = useRef<HTMLDivElement>(null);
  const minuteColumn = useRef<HTMLDivElement>(null);
  const restrictions = disabledTime?.();
  const disabled = inputDisabled || restrictions === true;
  const rules = typeof restrictions === "object" ? restrictions : undefined;
  const normalizedDraft = /^\d{1,2}:[0-5]\d$/.test(draft.trim())
    ? normalizeExtendedTime(draft)
    : "";
  const hour = normalizedDraft ? Number(normalizedDraft.split(":")[0]) : 0;
  const minute = normalizedDraft ? Number(normalizedDraft.split(":")[1]) : 0;

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    // Scroll the columns without moving the surrounding table/page.
    [hourColumn.current, minuteColumn.current].forEach((column) => {
      const selected = column?.querySelector<HTMLElement>(
        '[aria-selected="true"]',
      );
      if (column && selected) column.scrollTop = selected.offsetTop;
    });
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const isTimeDisabled = (selectedHour: number, selectedMinute: number) =>
    disabled ||
    selectedHour < 0 ||
    selectedHour >= extendedTimeHourCount ||
    !!rules?.disabledHours?.()?.includes(selectedHour) ||
    !!rules?.disabledMinutes?.(selectedHour)?.includes(selectedMinute) ||
    !!rules?.disabledSeconds?.(selectedHour, selectedMinute)?.includes(0);

  const canConfirm = !!normalizedDraft && !isTimeDisabled(hour, minute);
  const confirm = () => {
    const latest = disabledTime?.();
    if (
      !canConfirm ||
      latest === true ||
      (latest &&
        (latest.disabledHours?.()?.includes(hour) ||
          latest.disabledMinutes?.(hour)?.includes(minute) ||
          latest.disabledSeconds?.(hour, minute)?.includes(0)))
    ) {
      return;
    }
    if (!isControlled) setInternalValue(normalizedDraft);
    setDraft(normalizedDraft);
    setOpen(false);
    if (normalizedDraft !== value) onChange?.(normalizedDraft);
  };

  const cancel = () => {
    setDraft(value);
    setOpen(false);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
    if (event.key === "ArrowDown" && !open) {
      event.preventDefault();
      setDraft(value);
      setOpen(true);
    } else if (event.key === "Enter") {
      event.preventDefault();
      confirm();
    } else if (event.key === "Escape") {
      event.preventDefault();
      cancel();
    }
  };

  const panel = (
    <PickerPanel
      role="dialog"
      aria-label="時刻を選択"
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") {
          event.preventDefault();
          cancel();
        }
      }}
    >
      <div className="time-columns">
        <div
          className="time-column"
          ref={hourColumn}
          role="listbox"
          aria-label="時"
        >
          {listHours.map((item) => (
            <button
              key={item}
              type="button"
              role="option"
              className="item-option"
              aria-selected={!!normalizedDraft && hour === item}
              disabled={
                disabled ||
                !!rules?.disabledHours?.()?.includes(item) ||
                !MINUTES.some((itemMinute) => !isTimeDisabled(item, itemMinute))
              }
              onClick={() => {
                const nextMinute = !isTimeDisabled(item, minute)
                  ? minute
                  : MINUTES.find(
                      (candidate) => !isTimeDisabled(item, candidate),
                    );
                if (nextMinute !== undefined) {
                  setDraft(`${pad(item)}:${pad(nextMinute)}`);
                }
              }}
            >
              {pad(item)}
            </button>
          ))}
        </div>
        <div
          className="time-column"
          ref={minuteColumn}
          role="listbox"
          aria-label="分"
        >
          {MINUTES.map((item) => (
            <button
              key={item}
              type="button"
              role="option"
              className="item-option"
              aria-selected={!!normalizedDraft && minute === item}
              disabled={isTimeDisabled(hour, item)}
              onClick={() => setDraft(`${pad(hour)}:${pad(item)}`)}
            >
              {pad(item)}
            </button>
          ))}
        </div>
      </div>
      <div className="time-footer">
        <Button
          size="small"
          type="primary"
          disabled={!canConfirm}
          onClick={confirm}
        >
          決定
        </Button>
      </div>
    </PickerPanel>
  );

  return (
    <Popover
      content={panel}
      trigger="click"
      placement="bottomLeft"
      open={open && !disabled}
      onOpenChange={(visible) => {
        if (visible && !disabled) {
          setDraft(value);
          setOpen(true);
        } else {
          cancel();
        }
      }}
    >
      <PickerInput
        aria-label="時刻"
        aria-placeholder="時間を選択"
        {...inputProps}
        allowClear={allowClear}
        aria-haspopup="dialog"
        aria-expanded={open && !disabled}
        value={open ? draft : value}
        placeholder={placeholder}
        maxLength={5}
        disabled={disabled}
        suffix={<ClockCircleOutlined />}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (allowClear && event.type === "click" && !nextValue) {
            event.stopPropagation();
            if (!isControlled) setInternalValue("");
            setDraft("");
            setOpen(false);
            if (value) onChange?.("");
            return;
          }
          setDraft(nextValue);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
    </Popover>
  );
};

export default ExtendedTimePicker;
